import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { FaBriefcase, FaCheckCircle, FaMoneyBillWave, FaTimesCircle, FaCalendar } from 'react-icons/fa'
import { designTokens } from '@/styles/designTokens'

// Função para gerar o gradiente de fundo com base no status
const getStatusGradient = (status) => {
  // Cinza um pouco mais claro que o usado no OpportunitiesPanel (rgba(43, 43, 43, 0.3))
  const lightGray = 'rgba(55, 55, 55, 0.4)';
  let statusColor;

  switch (status) {
    case 'accepted':
      statusColor = designTokens.colors.status.warning; // Cor forte amarela/laranja
      break;
    case 'pending':
      statusColor = designTokens.colors.status.info; // Cor forte azul
      break;
    case 'billed':
      statusColor = designTokens.colors.status.success; // Cor forte verde
      break;
    default:
      return designTokens.colors.gradients.card; // Retorna o gradiente padrão se não houver status
  }

  // Gradiente da cor forte do status para o cinza claro
  const statusColorWithAlpha = statusColor.startsWith('#') ? `${statusColor}80` : statusColor;
  return `linear-gradient(135deg, ${statusColor} 0%, ${statusColorWithAlpha} 25%, ${lightGray} 100%)`;
};


// Card redesignado com fundo neutro e barra lateral de status
const Card = styled(motion.div)`
  background: ${({ $status }) => getStatusGradient($status)};
  border: 1px solid ${designTokens.colors.neutral.border};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing.lg};
  transition: ${designTokens.transitions.normal};
  height: 20%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
  font-family: ${designTokens.typography.fontFamily.primary};
  box-shadow: ${designTokens.shadows.sm};
  
  &:hover {
    background: rgba(70, 70, 75, 0.3);
    border-color: ${designTokens.colors.neutral.borderAccent};
    transform: translateY(-2px);
    box-shadow: ${designTokens.shadows.md};
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => {
      switch (props.$status) {
        case 'accepted': return designTokens.colors.status.warning
        case 'pending': return designTokens.colors.status.info
        case 'billed': return designTokens.colors.status.success
        default: return designTokens.colors.status.neutral
      }
    }};
    border-radius: 0 ${designTokens.borderRadius.sm} ${designTokens.borderRadius.sm} 0;
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${designTokens.spacing.md};
  gap: ${designTokens.spacing.md};
`

const ClientName = styled.h4`
  color: ${designTokens.colors.text.primary};
  margin: 0;
  font-size: ${designTokens.typography.fontSize.lg};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  line-height: ${designTokens.typography.lineHeight.tight};
  flex: 1;
  text-transform: capitalize;
`

const StatusIcon = styled.div`
  color: ${props => {
    switch (props.$status) {
      case 'accepted': return designTokens.colors.status.warning
      case 'pending': return designTokens.colors.status.info
      case 'billed': return designTokens.colors.status.success
      default: return designTokens.colors.status.neutral
    }
  }};
  font-size: ${designTokens.typography.fontSize.xl};
  flex-shrink: 0;
  filter: drop-shadow(0 0 4px ${props => {
    switch (props.$status) {
      case 'accepted': return designTokens.colors.status.warning + '60'
      case 'pending': return designTokens.colors.status.info + '60'
      case 'billed': return designTokens.colors.status.success + '60'
      default: return designTokens.colors.status.neutral + '60'
    }
  }});
`

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${designTokens.spacing.sm};
`

const Description = styled.p`
  font-size: ${designTokens.typography.fontSize.base};
  font-weight: ${designTokens.typography.fontWeight.regular};
  color: ${designTokens.colors.text.secondary};
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  font-family: ${designTokens.typography.fontFamily.primary};
  line-height: ${designTokens.typography.lineHeight.normal};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${designTokens.spacing.sm};
  padding-top: ${designTokens.spacing.sm};
  border-top: 1px solid ${designTokens.colors.neutral.border};
`

const ValueDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.xs};
  font-size: ${designTokens.typography.fontSize.base};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  color: ${designTokens.colors.text.primary};
`

const DateDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.xs};
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.secondary};
  font-weight: ${designTokens.typography.fontWeight.regular};

`



// Função utilitária para formatação de moeda
const formatCurrency = (value) => {
  if (!value) return 'N/A'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

// Função utilitária para formatação de data
const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  
  // Tentar diferentes formatos de data
  let date = new Date(dateString)
  
  // Se a data é inválida, tentar parseamento manual para formatos comuns
  if (isNaN(date.getTime())) {
    // Tentar formato ISO com T
    if (typeof dateString === 'string' && dateString.includes('T')) {
      date = new Date(dateString.replace('T', ' '))
    }
    // Tentar formato brasileiro DD/MM/YYYY
    else if (typeof dateString === 'string' && dateString.includes('/')) {
      const parts = dateString.split('/')
      if (parts.length === 3) {
        // Assumir DD/MM/YYYY e converter para YYYY-MM-DD
        date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`)
      }
    }
    // Tentar formato YYYY-MM-DD
    else if (typeof dateString === 'string' && dateString.includes('-')) {
      date = new Date(dateString)
    }
  }
  
  // Se ainda é inválida, retornar o valor original como string
  if (isNaN(date.getTime())) {
    console.log('Data inválida recebida:', dateString)
    return dateString || 'Data inválida'
  }
  
  // Formato: DD/M/YY HH:MM:SS (seguindo o padrão da imagem)
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear().toString().slice(-2)
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')
  
  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`
}

// Função para obter ícone do status
const getStatusIcon = (status) => {
  switch (status) {
    case 'pending': return <FaBriefcase />
    case 'accepted': return <FaCheckCircle /> 
    case 'billed': return <FaMoneyBillWave />
    default: return <FaTimesCircle />
  }
}

const OpportunityCard = React.memo(({ opportunity }) => {
  const { client, description, value, status, created_at, data, bubble_id } = opportunity

  // Debug temporário para verificar o formato das datas
  console.log('OpportunityCard - dados recebidos:', { 
    data, 
    created_at, 
    bubble_id,
    dataType: typeof data,
    created_atType: typeof created_at
  })

  return (
    <Card
      $status={status}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.2 }}
    >
      <CardHeader>
        <ClientName>{client || 'Cliente não informado'}</ClientName>
        <StatusIcon $status={status}>
          {getStatusIcon(status)}
        </StatusIcon>
      </CardHeader>
      
      <CardBody>
        {description && (
          <Description>{description}</Description>
        )}
      </CardBody>
      
      <CardFooter>
        {value && (
          <ValueDisplay>
            <FaMoneyBillWave />
            {formatCurrency(value)}
          </ValueDisplay>
        )}
        
        <DateDisplay>
          <FaCalendar />
          {bubble_id ? `${bubble_id} - ` : ''}{formatDate(data || created_at)}
        </DateDisplay>
      </CardFooter>
    </Card>
  )
})

export default OpportunityCard